import { normalizeMoodIconKey, type MoodIconKey } from '@nexacore/config/moodIconKeys';
import type { PublicMood, PublicMoodRecommendation } from './discoveryApi';
import type { PublicProduct } from '@/features/menu/types';

export interface StorefrontMood {
  id: string;
  locale: string;
  code: string;
  label: string;
  description: string;
  message: string;
  iconKey: MoodIconKey;
}

export interface StorefrontMoodRecommendation {
  id: string;
  title: string;
  description?: string;
}

/**
 * Customer-owned experience metadata. The public API decides which moods are active and supplies
 * their approved customer copy; this map only provides non-commercial fallback messages.
 * It never selects, ranks, prices, or recommends products.
 */
export const moodExperienceDefinitions = {
  positive: {
    code: 'POSITIVE',
    fallbackMessage: 'Giữ nhịp tích cực với một khoảng nghỉ thật ngon.',
  },
  tired: {
    code: 'TIRED',
    fallbackMessage: 'Một chút ngọt ngào có thể giúp bạn lấy lại nhịp nghỉ.',
  },
  low_mood: {
    code: 'LOW_MOOD',
    fallbackMessage: 'Chọn một khoảng nghỉ nhỏ theo nhịp của riêng bạn.',
  },
  healing: {
    code: 'HEALING',
    fallbackMessage: 'Dành một chút thời gian thư giãn theo cách bạn thấy phù hợp.',
  },
  focus: {
    code: 'FOCUS',
    fallbackMessage: 'Một nhịp nghỉ gọn nhẹ để tiếp tục điều bạn đang làm.',
  },
  celebrate: {
    code: 'CELEBRATE',
    fallbackMessage: 'Tận hưởng khoảnh khắc vui theo cách của riêng bạn.',
  },
} as const satisfies Record<MoodIconKey, { code: string; fallbackMessage: string }>;

export type StorefrontMoodDefinition =
  (typeof moodExperienceDefinitions)[keyof typeof moodExperienceDefinitions];

export function moodExperienceDefinition(code?: string): StorefrontMoodDefinition | undefined {
  const normalized = code?.trim().toUpperCase();
  return Object.values(moodExperienceDefinitions).find(
    (definition) => definition.code === normalized
  );
}

export function toStorefrontMood(mood: PublicMood): StorefrontMood | null {
  const id = mood.moodDefinitionId;
  const code = mood.code?.trim().toUpperCase();
  const label = mood.name?.trim();
  const definition = moodExperienceDefinition(code);
  const message = mood.message?.trim() || definition?.fallbackMessage;
  if (!id || !code || !label || !message) return null;

  return {
    id,
    locale: mood.locale || 'vi-VN',
    code,
    label,
    description: mood.description?.trim() || 'Gợi ý trải nghiệm từ cửa hàng',
    message,
    iconKey: normalizeMoodIconKey(mood.iconKey),
  };
}

export function toStorefrontMoodRecommendation(
  item: PublicMoodRecommendation
): StorefrontMoodRecommendation | null {
  const title = item.title?.trim();
  if (!title) return null;
  return {
    id: item.productId ?? item.categoryId ?? item.targetId ?? `${item.moodCode}-${title}`,
    title,
    description: item.description?.trim() || undefined,
  };
}

export function moodRecommendationToProduct(
  item: PublicMoodRecommendation,
  moodName?: string
): PublicProduct | null {
  if (!item.productId || !item.title) return null;
  return {
    id: item.productId,
    categoryId: item.categoryId,
    moodCode: item.moodCode,
    moodName,
    name: item.title,
    description: item.description,
    availability: item.availability ?? 'Available',
    fromPrice: item.fromPrice,
    currency: item.currency,
    media: item.primaryMediaRef
      ? [
          {
            reference: item.primaryMediaRef,
            mimeType: item.primaryMediaMimeType,
            publicUrl: item.primaryMediaPublicUrl,
          },
        ]
      : [],
    variants: [],
    modifierGroups: [],
  } as PublicProduct;
}
